import { useEffect, useRef, useState, type ReactNode } from 'react'
import {
  api,
  type Campaign,
  type Cycle,
  type DebugRecipient,
  type Draft,
  type DraftRecipient,
  type DraftRow,
  type DryRunResult,
  type Job,
  type SendAuthorizationState,
  type StyleInfo,
} from '../api'
import { useNotice } from '../useNotice'
import { diffWords, unchanged, type Part } from '../diff'
import { CycleBadge, StaleBadge, age } from './Campaigns'

/**
 * One cycle — one tick of a campaign — and everything that happens to it after
 * the scan: who was drafted and who was skipped and why, recompose, refine,
 * review, and send.
 *
 * These panels used to be the steps of the v2 wizard. The wizard is gone; a
 * cycle is where its work happens now, reached from the campaign that owns it.
 */
type CyclePane = 'record' | 'people' | 'review' | 'send'

function initialCyclePane(status: Cycle['status']): CyclePane {
  if (status === 'sent' || status === 'cancelled' || status === 'failed') return 'record'
  if (status === 'approved' || status === 'dispatching') return 'send'
  return 'review'
}

function isClosedCycle(status: Cycle['status']): boolean {
  return status === 'sent' || status === 'cancelled' || status === 'failed'
}

export function CycleView({ cycleId, onBack }: { cycleId: number; onBack: () => void }) {
  const [cycle, setCycle] = useState<Cycle | null>(null)
  const [campaign, setCampaign] = useState<Campaign | null>(null)
  /** null until the cycle loads — then default from status (sent → record). */
  const [view, setView] = useState<CyclePane | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [cancelling, setCancelling] = useState(false)

  const load = async () => {
    try {
      setError(null)
      const cy = await api.cycle(cycleId)
      setCycle(cy)
      setCampaign(await api.campaign(cy.campaign_id))
    } catch (err) {
      setError(String((err as Error).message))
    }
  }

  useEffect(() => {
    setView(null)
    setConfirmCancel(false)
    void load()
  }, [cycleId])

  if (error && !cycle) return <div className="error">{error}</div>
  if (!cycle) return <div className="loading">Loading cycle…</div>

  const pane = view ?? initialCyclePane(cycle.status)
  const closed = isClosedCycle(cycle.status)
  // A cycle stuck in `dispatching` may already have mailed some of its people;
  // the sendlog has to be read before anything is closed over it (D106).
  const canCancel = !closed && cycle.status !== 'dispatching'

  const cancel = async () => {
    setCancelling(true)
    setError(null)
    try {
      await api.cancelCycle(cycle.id)
      setConfirmCancel(false)
      setView(null)
      await load()
    } catch (err) {
      setError(String((err as Error).message))
    } finally {
      setCancelling(false)
    }
  }

  const steps: { id: CyclePane; n: string; value: string; caption: string }[] = [
    { id: 'people', n: '1 · Who', value: cycle.members.toLocaleString(), caption: `matched · ${cycle.skipped.toLocaleString()} skipped` },
    {
      id: 'review',
      n: '2 · Review',
      value: cycle.awaiting_review.toLocaleString(),
      caption: `waiting of ${cycle.drafts.toLocaleString()} drafts`,
    },
    {
      id: 'send',
      n: '3 · Send',
      value: closed ? cycle.sent.toLocaleString() : cycle.approved.toLocaleString(),
      caption: closed ? 'sent' : `approved · ${cycle.sent.toLocaleString()} sent`,
    },
  ]
  if (closed) steps.push({ id: 'record', n: 'Record', value: cycle.status.replace('_', ' '), caption: cycle.ended_at ? `ended ${new Date(cycle.ended_at).toLocaleDateString()}` : 'finished' })

  return (
    <>
      <div className="page-head">
        <button className="linkish crumb" onClick={onBack}>← {cycle.campaign_name}</button>
        <h1>Cycle {cycle.id}</h1>
        <CycleBadge status={cycle.status} />
        <StaleBadge kind={cycle.stale_kind} />
        <span className="sub">
          started {new Date(cycle.started_at).toLocaleString()} · by {cycle.triggered_by}
        </span>
      </div>
      {error && <div className="error">{error}</div>}
      {cycle.stale && (
        <div className="notice warn">
          <strong>Open for {age(cycle.age_hours)}.</strong> {cycle.stale_reason}.
          {cycle.holding > 0 && (
            <>
              {' '}
              While it stays open, {cycle.holding === 1 ? 'that account is' : `those ${cycle.holding} accounts are`}{' '}
              skipped as <span className="mono">in_flight</span> by every later cycle of this
              campaign (D86).
            </>
          )}
          <div className="hint" style={{ marginTop: 6 }}>{cycle.what_to_do}</div>
        </div>
      )}

      <nav className="steps" aria-label="Cycle progress">
        {steps.map((s) => (
          <button key={s.id} aria-current={pane === s.id} onClick={() => setView(s.id)}>
            <span className="step-n">{s.n}</span>
            <span className="step-v">{s.value}</span>
            <span className="step-c">{s.caption}</span>
          </button>
        ))}
      </nav>

      <div className="with-rail slim">
        <div>
          {pane === 'record' && (
            <RecordStep
              cycle={cycle}
              style={campaign?.style ?? null}
              onOpenPeople={() => setView('people')}
              onOpenDrafts={() => setView('review')}
            />
          )}
          {pane === 'people' && <PeopleStep cycle={cycle} />}
          {pane === 'review' && (
            <ReviewStep cycleId={cycleId} campaignId={cycle.campaign_id} style={campaign?.style ?? null} closed={closed} onDone={load} />
          )}
          {pane === 'send' && (
            <SendStep cycleId={cycleId} style={campaign?.style ?? null} onOpenCampaign={onBack} onChanged={load} />
          )}
        </div>

        <aside className="rail" aria-label="Cycle status and actions">
          <CycleNextCard cycle={cycle} pane={pane} onGo={setView} />

          {pane === 'review' && !closed && (
            <div className="panel">
              <h2>Keys</h2>
              <div className="panel-body">
                <dl className="facts">
                  <dt><span className="kbd">j</span> <span className="kbd">k</span></dt><dd>next / previous</dd>
                  <dt><span className="kbd">a</span></dt><dd>approve, then next</dd>
                  <dt><span className="kbd">r</span></dt><dd>reject, then next</dd>
                  <dt><span className="kbd">v</span></dt><dd>text / rendered</dd>
                </dl>
              </div>
            </div>
          )}

          <div className="panel">
            <h2>This cycle</h2>
            <div className="panel-body">
              <dl className="facts">
                <dt>Drafted</dt><dd>{cycle.drafts.toLocaleString()}</dd>
                <dt>Approved</dt><dd>{cycle.approved.toLocaleString()}</dd>
                <dt>Rejected</dt><dd>{cycle.rejected.toLocaleString()}</dd>
                <dt>Sent</dt><dd>{cycle.sent.toLocaleString()}</dd>
                {cycle.holding > 0 && (<><dt>Holding</dt><dd title="accounts no later cycle of this campaign will draft until this one closes">{cycle.holding.toLocaleString()}</dd></>)}
                <dt>Style</dt>
                <dd>
                  <span className="mono">{campaign?.style ?? 'default'}</span>
                  <div><button className="linkish" onClick={onBack}>change on the campaign</button></div>
                </dd>
              </dl>
              {canCancel && (
                confirmCancel ? (
                  <div className="notice warn">
                    Close this cycle without sending? Its {cycle.holding > 0 ? `${cycle.holding} people go` : 'people go'} back to the
                    campaign for a later cycle. Nothing is sent.
                    <div className="row" style={{ marginTop: 'var(--s2)' }}>
                      <button className="danger" disabled={cancelling} onClick={() => void cancel()}>
                        {cancelling ? 'Cancelling…' : 'Yes, cancel it'}
                      </button>
                      <button disabled={cancelling} onClick={() => setConfirmCancel(false)}>Keep it</button>
                    </div>
                  </div>
                ) : (
                  <button className="block" onClick={() => setConfirmCancel(true)}>Cancel this cycle</button>
                )
              )}
            </div>
          </div>
        </aside>
      </div>
    </>
  )
}

/** The rail's lead card: the one thing this cycle is waiting for. */
function CycleNextCard({ cycle, pane, onGo }: { cycle: Cycle; pane: CyclePane; onGo: (p: CyclePane) => void }) {
  let headline: string
  let detail: string
  let go: { label: string; pane: CyclePane } | null = null
  if (isClosedCycle(cycle.status)) {
    headline = `Finished · ${cycle.status.replace('_', ' ')}`
    detail = 'This is the record. Change audience, copy or look on the campaign.'
  } else if (cycle.awaiting_review > 0) {
    headline = `${cycle.awaiting_review.toLocaleString()} draft${cycle.awaiting_review === 1 ? '' : 's'} to review`
    detail = 'Nothing sends until a draft is approved.'
    if (pane !== 'review') go = { label: 'Review drafts', pane: 'review' }
  } else if (cycle.approved > 0) {
    headline = `${cycle.approved.toLocaleString()} approved, ready to send`
    detail = 'Send to the test inbox first, then to customers.'
    if (pane !== 'send') go = { label: 'Go to send', pane: 'send' }
  } else {
    headline = cycle.status.replace('_', ' ')
    detail = 'Nothing is waiting on you in this cycle.'
  }
  return (
    <div className={`panel lead${cycle.stale ? ' warn' : ''}`}>
      <h2>Next step</h2>
      <div className="panel-body">
        <strong>{headline}</strong>
        <p className="hint tight-above">{detail}</p>
        {go && (
          <button className="primary block" onClick={() => onGo(go!.pane)}>{go.label} →</button>
        )}
      </div>
    </div>
  )
}

/** Finished cycle: what happened, not the workplace for editing the campaign. */
function RecordStep({
  cycle,
  style,
  onOpenPeople,
  onOpenDrafts,
}: {
  cycle: Cycle
  style: string | null
  onOpenPeople: () => void
  onOpenDrafts: () => void
}) {
  const ended = cycle.ended_at ? new Date(cycle.ended_at).toLocaleString() : null
  return (
    <div className="panel">
      <h2>What this cycle did</h2>
      <div className="panel-body">
        <p className="hint">
          This cycle is finished. The lists below are the record — change audience, copy, or style
          on the campaign, not here.
        </p>
        <dl className="facts">
          <dt>Status</dt>
          <dd>
            <CycleBadge status={cycle.status} />
            {ended && <span className="dim"> · ended {ended}</span>}
          </dd>
          <dt>People drafted</dt>
          <dd className="num">{cycle.members}</dd>
          <dt>Skipped</dt>
          <dd className="num">{cycle.skipped}</dd>
          <dt>Drafts</dt>
          <dd className="num">{cycle.drafts}</dd>
          <dt>Approved</dt>
          <dd className="num">{cycle.approved}</dd>
          <dt>Rejected</dt>
          <dd className="num">{cycle.rejected}</dd>
          <dt>Sent</dt>
          <dd className="num">{cycle.sent}</dd>
          <dt>Style used</dt>
          <dd className="mono">{style ?? <span className="dim">default</span>}</dd>
        </dl>
        <div className="row">
          <button type="button" onClick={onOpenPeople}>
            Who was drafted &amp; skipped
          </button>
          <button type="button" onClick={onOpenDrafts}>
            Open drafts
          </button>
        </div>
      </div>
    </div>
  )
}

/** Who this cycle drafted, and who it did not — with the reason for each. */
function PeopleStep({ cycle }: { cycle: Cycle }) {
  const skips = cycle.skips ?? []
  const byReason = new Map<string, number>()
  for (const s of skips) byReason.set(s.reason, (byReason.get(s.reason) ?? 0) + 1)
  return (
    <>
      <div className="panel">
        <h2>People</h2>
        <div className="panel-body">
          <dl className="facts">
            <dt>Matched</dt>
            <dd>{cycle.members.toLocaleString()} accounts matched the filter when this cycle scanned{cycle.run_id != null && <> — saved list <span className="mono">run {cycle.run_id}</span></>}</dd>
            <dt>Drafted</dt>
            <dd>{cycle.drafts.toLocaleString()}</dd>
            <dt>Skipped</dt>
            <dd>
              {skips.length === 0 ? 'nobody' : [...byReason.entries()].map(([r, n]) => `${n} ${r.replace('_', ' ')}`).join(', ')}
            </dd>
          </dl>
          <p className="hint">
            Cooldown means this campaign wrote to them too recently; in flight means an earlier
            cycle still holds an unsent draft for them; account cap is the across-campaign limit.
            Add or remove people on the People tab against the saved list above.
          </p>
        </div>
        {skips.length > 0 && (
          <table>
            <thead>
              <tr><th className="num">Account</th><th>Reason</th><th>Detail</th></tr>
            </thead>
            <tbody>
              {skips.slice(0, 300).map((s) => (
                <tr key={s.account_id}>
                  <td className="num mono">{s.account_id}</td>
                  <td><span className="badge catchall">{s.reason.replace('_', ' ')}</span></td>
                  <td className="dim">{s.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}

/**
 * Recompose, and the compose problems, above the queue they describe.
 *
 * Composing itself happens when the campaign is created — it is
 * deterministic, needs no model and no decision. RECOMPOSING is a decision:
 * it rebuilds every draft from the current copy and DISCARDS refinements, so
 * it stays an explicit act with its consequence written next to it.
 */
function RecomposePanel({ cycleId, onDone }: { cycleId: number; onDone: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<Awaited<ReturnType<typeof api.compose>> | null>(null)
  const [open, setOpen] = useState(false)

  const run = async (replace: boolean) => {
    setBusy(true)
    setError(null)
    try {
      setResult(await api.compose(cycleId, replace))
      onDone()
    } catch (err) {
      setError(String((err as Error).message))
    } finally {
      setBusy(false)
    }
  }

  const problems = result?.problems ?? []

  return (
    <div className="panel">
      <h2>
        Composed copy
        {result ? (
          <span className="count">
            {result.composed} rebuilt
            {result.with_problems ? `, ${result.with_problems} with problems` : ''}
          </span>
        ) : null}
      </h2>
      <div className="panel-body">
        {error && <div className="error">{error}</div>}
        <div className="row">
          <button disabled={busy} onClick={() => void run(true)}>
            {busy ? (
              <span className="row" style={{ gap: 7 }}>
                <span className="spinner" /> Recomposing…
              </span>
            ) : (
              'Recompose all'
            )}
          </button>
          <button disabled={busy} onClick={() => void run(false)}>
            Compose anything missing
          </button>
          <span className="hint">
            Drafts are built when the cycle ticks. Recomposing rebuilds them from the
            campaign's current copy and <strong>discards every refinement</strong>, returning each draft to
            pending.
          </span>
        </div>

        {problems.length > 0 && (
          <div className="notice">
            <div className="row">
              <button onClick={() => setOpen(!open)}>
                {open ? '▾' : '▸'} {problems.length} with problems
              </button>
              <span className="hint">
                Fallback copy means neither the campaign's slots nor this segment's fragments cover
                that position — set the slot on the campaign, or approve the fragment, then
                recompose.
              </span>
            </div>
            {open && (
              <ul className="bullets">
                {problems.map((p) => (
                  <li key={p.account_id} className="mono" style={{ fontSize: 12 }}>
                    {p.account_id}: {p.problems.join('; ')}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * The refine controls, now a section above the queue rather than a step.
 *
 * Collapsed by default: the system prompt is 13,000 characters and pushing
 * the drafts below the fold to show instructions nobody is editing was most
 * of what made the old step feel like a detour. Open it when you want to
 * change how the model is briefed.
 */
function RefineControls({ cycleId, campaignId, onDone }: { cycleId: number; campaignId: number; onDone: () => void }) {
  const [open, setOpen] = useState(false)
  const [campaign, setCampaign] = useState<Campaign | null>(null)
  const [prompt, setPrompt] = useState('')
  const [savePrompt, setSavePrompt] = useState(false)
  const [job, setJob] = useState<Job | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sample, setSample] = useState(10)
  const [skipRefined, setSkipRefined] = useState(false)
  const [allowGeneric, setAllowGeneric] = useState(false)
  const timer = useRef<number | null>(null)

  useEffect(() => {
    void (async () => {
      const c = await api.campaign(campaignId)
      setCampaign(c)
      setPrompt(c.system_prompt ?? '')
    })()
  }, [campaignId])

  useEffect(() => () => {
    if (timer.current) window.clearInterval(timer.current)
  }, [])

  const start = async (mode: 'one' | 'sample' | 'all' | 'retry') => {
    setBusy(true)
    setError(null)
    setJob(null)
    try {
      const edited = campaign && prompt !== (campaign.system_prompt ?? '')
      const started = await api.refine(cycleId, {
        system_prompt: edited ? prompt : undefined,
        save_prompt: edited ? savePrompt : false,
        draft_id: mode === 'one' ? await firstDraftId(cycleId) : undefined,
        limit: mode === 'sample' ? sample : undefined,
        // 'retry' is for after a prompt edit: the rows the validator refused
        // are the ones worth re-running, and the accepted ones are not.
        status_filter: mode === 'retry' ? 'pending' : undefined,
        only_unrefined: mode === 'sample' || mode === 'retry' ? true : skipRefined,
        allow_generic: allowGeneric,
      })
      const poll = async () => {
        const j = await api.job(started.job_id)
        setJob(j)
        if (j.status !== 'running') {
          if (timer.current) window.clearInterval(timer.current)
          timer.current = null
          setBusy(false)
          onDone()
        }
      }
      await poll()
      timer.current = window.setInterval(() => void poll(), 2500)
    } catch (err) {
      setError(String((err as Error).message))
      setBusy(false)
    }
  }

  const modelUsed = job?.detail?.model ? String(job.detail.model) : null

  return (
    <div className="panel">
      <h2>
        Refine with the AI
        {modelUsed && <span className="count mono">{modelUsed}</span>}
      </h2>
      <div className="panel-body">
        {error && <div className="error">{error}</div>}

        {/* One row of verbs. Everything that is a knob rather than an action
            moved behind Options: four buttons, two checkboxes and three
            paragraphs of explanation pushed the queue this screen is
            actually about below the fold. */}
        <div className="row">
          <button className="primary" disabled={busy} onClick={() => void start('all')}>
            {busy ? 'Refining…' : 'Refine all'}
          </button>
          <button disabled={busy} onClick={() => void start('retry')}>
            Only the unrefined
          </button>
          <button disabled={busy} onClick={() => void start('one')}>
            Try one
          </button>
          <button onClick={() => setOpen(!open)}>{open ? '▾' : '▸'} Options</button>
          {!open && (
            <span className="hint">
              Rewrites the wording of each draft. The original is kept either way.
            </span>
          )}
        </div>

        {open && (
          <div className="stack" style={{ borderTop: '1px solid var(--line)', paddingTop: 'var(--s3)' }}>
            <p className="hint">
              The model gets the composed draft plus this campaign's instructions, so it never sees
              a blank page and the worst case is copy that was already approved. Output that
              invents a number, states a legal position, rewords the button label, adds a link or
              drops a paragraph is rejected and the original stands.
            </p>

            <div className="row-form">
              <div style={{ width: 130 }}>
                <label htmlFor="sample">Sample size</label>
                <input
                  id="sample"
                  type="number"
                  min={1}
                  value={sample}
                  style={{ width: '100%' }}
                  onChange={(e) => setSample(Math.max(1, Number(e.target.value) || 1))}
                />
              </div>
              <div>
                <button disabled={busy} onClick={() => void start('sample')}>
                  Refine {sample} only
                </button>
              </div>
            </div>

            <div className="row">
              <label className="check">
                <input
                  type="checkbox"
                  style={{ width: 'auto' }}
                  checked={skipRefined}
                  onChange={(e) => setSkipRefined(e.target.checked)}
                />
                Skip drafts already refined
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  style={{ width: 'auto' }}
                  checked={allowGeneric}
                  onChange={(e) => setAllowGeneric(e.target.checked)}
                />
                Refine generic drafts too
              </label>
            </div>
            <p className="hint caption">
              Drafts composed mostly from generic fallbacks are skipped unless you tick the box:
              improving the wording of copy aimed at the wrong segment gives you a better-written
              wrong email. Approve that segment's fragments instead.
            </p>

            <div className="field">
              <label htmlFor="prompt">
                Instructions for this campaign{' '}
                <span className="label-note">
                  — {prompt.length.toLocaleString()} characters, from the campaign
                </span>
              </label>
              <textarea
                id="prompt"
                rows={12}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                spellCheck={false}
              />
              {campaign && prompt !== (campaign.system_prompt ?? '') && (
                <label className="check" style={{ marginTop: 'var(--s2)' }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={savePrompt}
                    onChange={(e) => setSavePrompt(e.target.checked)}
                  />
                  Also save these instructions onto the campaign for future cycles (withdraws copy approval)
                </label>
              )}
            </div>
          </div>
        )}

        {job && <RefineProgress job={job} />}
      </div>
    </div>
  )
}

async function firstDraftId(cycleId: number): Promise<number | undefined> {
  const page = await api.drafts({ cycle_id: cycleId, limit: 1 })
  return page.drafts[0]?.id
}

/**
 * Failures collapsed to one line per cause.
 *
 * Ten drafts hitting the same rate limit is one fact about the provider, not
 * ten facts about the drafts. The first version printed the full error for
 * each, which filled the panel with repeated JSON and buried the count.
 */
function groupByCause(
  failures: { draft_id: number; problems: string[] }[],
): [string, number[]][] {
  const groups = new Map<string, number[]>()
  for (const failure of failures) {
    // The first sentence is the cause; anything after a colon is usually the
    // provider echoing our request back at us.
    const raw = (failure.problems[0] ?? 'unknown error').trim()
    const cause = raw.length > 140 ? `${raw.slice(0, 137)}…` : raw
    const ids = groups.get(cause) ?? []
    ids.push(failure.draft_id)
    groups.set(cause, ids)
  }
  return [...groups.entries()].sort((a, b) => b[1].length - a[1].length)
}

function RefineProgress({ job }: { job: Job }) {
  const d = job.detail as Record<string, unknown>
  const rejections = (d.rejections as { draft_id: number; problems: string[] }[]) ?? []
  const skips = (d.skips as { draft_id: number; reason: string }[]) ?? []
  const failures = (d.failures as { draft_id: number; problems: string[] }[]) ?? []
  const skipped = Number(d.skipped_generic ?? 0)
  const failed = Number(d.failed ?? 0)
  return (
    <div className={job.status === 'failed' ? 'error' : 'notice'}>
      <div className="row">
        <span className={`badge ${job.status === 'running' ? 'running' : job.status === 'completed' ? 'ok' : 'bad'}`}>
          {job.status}
        </span>
        {d.done != null && (
          <span>
            {/* `skipped` belongs here, not only in the list below. Without it
                a fully-skipped run read "3 / 3 — 0 accepted, 0 rejected",
                which accounts for none of the three and looks like the model
                silently did nothing. */}
            {String(d.done)} / {String(d.total)} — {String(d.refined ?? 0)} accepted,{' '}
            {String(d.rejected ?? 0)} rejected
            {failed ? `, ${failed} failed` : ''}
            {skipped ? `, ${skipped} skipped` : ''}
          </span>
        )}
        {d.model ? <span className="mono">{String(d.model)}</span> : null}
      </div>
      {d.error ? (
        <div className="mono" style={{ marginTop: 'var(--s2)' }}>{String(d.error)}</div>
      ) : null}
      {failures.length > 0 && (
        <div style={{ marginTop: 'var(--s2)' }}>
          {/* Grouped by cause. Ten drafts failing for one reason is ONE fact
              about the provider, and printing the same error envelope ten
              times said less than a single line would — the panel filled
              with JSON braces and the count got lost among them. */}
          <div className="hint label-line">
            The model was never reached on {failures.length}{' '}
            {failures.length === 1 ? 'draft' : 'drafts'} — a provider or transport error, not a
            judgement about the copy. Rate limits are retried automatically with backoff, so
            reaching this list means the retries were exhausted too.{' '}
            <strong>Only the unrefined</strong> picks them up:
          </div>
          <ul className="bullets tight">
            {groupByCause(failures).map(([cause, ids]) => (
              <li key={cause} style={{ fontSize: 12 }}>
                <strong>{ids.length}×</strong> {cause}
                <div className="hint caption">
                  draft{ids.length === 1 ? '' : 's'} {ids.slice(0, 12).join(', ')}
                  {ids.length > 12 ? `, +${ids.length - 12} more` : ''}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {skips.length > 0 && (
        <div style={{ marginTop: 'var(--s2)' }}>
          <div className="hint label-line">
            Not sent to the model at all — nothing was spent on these:
          </div>
          <ul className="bullets tight">
            {skips.slice(0, 5).map((r) => (
              <li key={r.draft_id} style={{ fontSize: 12 }}>
                draft {r.draft_id}: {r.reason}
              </li>
            ))}
          </ul>
          {skips.length > 5 && (
            <div className="hint caption">
              …and {skips.length - 5} more for the same reason.
            </div>
          )}
        </div>
      )}
      {rejections.length > 0 && (
        <div style={{ marginTop: 'var(--s2)' }}>
          <div className="hint label-line">
            Rejected by the validator — the original copy is unchanged for these:
          </div>
          <ul className="bullets tight">
            {rejections.slice(0, 8).map((r) => (
              <li key={r.draft_id} style={{ fontSize: 12 }}>
                draft {r.draft_id}: {r.problems.join('; ')}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function ReviewStep({
  cycleId,
  campaignId,
  style,
  closed,
  onDone,
}: {
  cycleId: number
  campaignId: number
  style: string | null
  closed: boolean
  onDone: () => void
}) {
  const [rows, setRows] = useState<DraftRow[] | null>(null)
  const [total, setTotal] = useState(0)
  const [statusFilter, setStatusFilter] = useState('')
  const [open, setOpen] = useState<Draft | null>(null)
  const [chosen, setChosen] = useState<Set<number>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [refining, setRefining] = useState<number | null>(null)
  // Which face of the draft to show. Kept across drafts, so someone reading
  // rendered emails stays on rendered as they move down the queue.
  const [openView, setOpenView] = useState<'text' | 'rendered'>('text')
  const [loadingDraft, setLoadingDraft] = useState<number | null>(null)

  const openDraft = async (draftId: number) => {
    setLoadingDraft(draftId)
    try {
      setOpen(await api.draft(draftId))
    } catch (err) {
      setError(String((err as Error).message))
    } finally {
      setLoadingDraft(null)
    }
  }
  // Separate from `error` on purpose. `error` means the list could not be
  // loaded and the early return below replaces the screen with it. A per-row
  // refusal must NOT do that: losing the queue to read one row's message is
  // the opposite of useful when you are working through 31 of them.
  const [outcomeMsg, setOutcomeMsg] = useNotice(30000)
  // The draft a skip could be forced through, if the operator decides so.
  const [retryGeneric, setRetryGeneric] = useState<number | null>(null)
  const recipients = useRecipients(cycleId)

  /** Reload the queue; open `focus` (or keep the open draft, or the first). */
  const load = async (focus?: number | null) => {
    try {
      setError(null)
      const page = await api.drafts({
        cycle_id: cycleId,
        status_filter: statusFilter || undefined,
        limit: 200,
      })
      setRows(page.drafts)
      setTotal(page.total)
      setChosen(new Set())
      void recipients.reload()
      const ids = page.drafts.map((r) => r.id)
      const want = focus !== undefined ? focus : open?.id ?? null
      // Nothing asked for: start where the work is, not on a row already decided.
      const firstWaiting = page.drafts.find((r) => r.status === 'pending' || r.status === 'revised')?.id
      const target = want != null && ids.includes(want) ? want : firstWaiting ?? ids[0] ?? null
      if (target == null) setOpen(null)
      else if (target !== open?.id || focus !== undefined) await openDraft(target)
    } catch (err) {
      setError(String((err as Error).message))
    }
  }

  useEffect(() => {
    void load(null)
  }, [cycleId, statusFilter])

  // Refine one row from inside Review. Without it, noticing that one draft
  // reads badly meant leaving the queue and refining the whole cycle — so the
  // realistic response to one bad draft was to approve it anyway.
  const refineRow = async (draftId: number, allowGeneric = false) => {
    setBusy(true)
    setError(null)
    setRefining(draftId)
    // Held rather than shown immediately: load() below clears the error box,
    // so setting it first meant a skip or a rejection was displayed for a
    // few milliseconds and then wiped.
    let outcome: string | null = null
    let retryable: number | null = null
    try {
      const started = await api.refine(cycleId, {
        draft_id: draftId,
        allow_generic: allowGeneric,
      })
      // Short poll: one draft is one model call, not a batch.
      for (let i = 0; i < 40; i++) {
        const job = await api.job(started.job_id)
        if (job.status !== 'running') {
          const detail = job.detail as Record<string, unknown>
          const skipped = Number(detail.skipped_generic ?? 0)
          const rejected = Number(detail.rejected ?? 0)
          if (detail.error) {
            outcome = String(detail.error)
          } else if (skipped) {
            outcome =
              (detail.skips as { reason: string }[] | undefined)?.[0]?.reason ??
              'skipped: composed mostly from generic fallback copy'
            // Offered per row: you are looking at this one draft and choosing
            // for it alone.
            retryable = draftId
          } else if (rejected) {
            const problems =
              (detail.rejections as { problems: string[] }[] | undefined)?.[0]?.problems ?? []
            outcome = `the validator refused this rewrite, so the original stands: ${problems.join('; ')}`
          }
          break
        }
        await new Promise((r) => setTimeout(r, 1200))
      }
      await load(draftId)
      onDone()
    } catch (err) {
      outcome = String((err as Error).message)
    } finally {
      setRefining(null)
      setBusy(false)
      setRetryGeneric(retryable)
      if (outcome) setOutcomeMsg(outcome)
    }
  }

  /** The row after `id` in the queue, or before it at the end. */
  const neighbour = (id: number): number | null => {
    if (!rows) return null
    const i = rows.findIndex((r) => r.id === id)
    return rows[i + 1]?.id ?? rows[i - 1]?.id ?? null
  }

  const verdict = async (v: 'approved' | 'rejected' | 'pending', ids: number[], focus?: number | null) => {
    if (!ids.length) return
    setBusy(true)
    try {
      const out = await api.review(ids, v)
      if (out.read_alone?.length) {
        setOutcomeMsg(`${out.read_alone.length} not approved: health, finance or children's sites are approved one at a time — open each and approve it on its own.`)
      }
      await load(focus)
      onDone()
    } catch (err) {
      setError(String((err as Error).message))
    } finally {
      setBusy(false)
    }
  }

  // Decide on the open draft and move on. With a status filter on, the
  // decided row leaves the list, so "next" is the neighbour picked BEFORE the
  // reload rather than whatever lands at that index after it.
  const decide = (v: 'approved' | 'rejected') => {
    if (!open) return
    void verdict(v, [open.id], neighbour(open.id))
  }

  const step = (dir: 1 | -1) => {
    if (!rows || rows.length === 0) return
    const i = open ? rows.findIndex((r) => r.id === open.id) : -1
    const next = rows[Math.min(rows.length - 1, Math.max(0, i + dir))]
    if (next && next.id !== open?.id) void openDraft(next.id)
  }

  // j/k move, a/r decide, v flips text/rendered. Ignored while typing.
  const keys = useRef({ step, decide, busy, closed })
  keys.current = { step, decide, busy, closed }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      if (t && (t.closest('input, textarea, select') || t.isContentEditable)) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const k = keys.current
      if (e.key === 'j') k.step(1)
      else if (e.key === 'k') k.step(-1)
      else if (e.key === 'v') setOpenView((v) => (v === 'text' ? 'rendered' : 'text'))
      else if (!k.busy && !k.closed && e.key === 'a') k.decide('approved')
      else if (!k.busy && !k.closed && e.key === 'r') k.decide('rejected')
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  if (error) return <div className="error">{error}</div>
  if (!rows) return <div className="loading">Loading drafts…</div>

  const position = open ? rows.findIndex((r) => r.id === open.id) + 1 : 0

  return (
    <>
      {!closed && (
        <details className="fold" style={{ marginBottom: 'var(--s4)' }}>
          <summary>Tools — rebuild drafts, or rewrite them all with the AI</summary>
          <div style={{ marginTop: 'var(--s3)' }}>
            <RecomposePanel cycleId={cycleId} onDone={() => { void load(); onDone() }} />
            <RefineControls cycleId={cycleId} campaignId={campaignId} onDone={() => { void load(); onDone() }} />
          </div>
        </details>
      )}

      <div className="review-split">
        <div className="panel" style={{ marginBottom: 0 }}>
          <h2>
            Drafts <span className="count">{total}</span>
            <select
              aria-label="Show drafts"
              value={statusFilter}
              style={{ marginLeft: 'auto', padding: '2px 6px' }}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">all</option>
              <option value="pending">pending</option>
              <option value="revised">revised</option>
              <option value="approved">approved</option>
              <option value="rejected">rejected</option>
            </select>
          </h2>
          {!closed && (
            <div className="panel-body compact">
              <div className="row" style={{ gap: 6 }}>
                <label className="check" style={{ fontSize: 12 }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={chosen.size === rows.length && rows.length > 0}
                    onChange={(e) => setChosen(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())}
                  />
                  all
                </label>
                <button disabled={busy || chosen.size === 0} onClick={() => void verdict('approved', [...chosen])}>
                  Approve {chosen.size || ''}
                </button>
                <button className="danger" disabled={busy || chosen.size === 0} onClick={() => void verdict('rejected', [...chosen])}>
                  Reject
                </button>
              </div>
            </div>
          )}
          {rows.length === 0 ? (
            <div className="empty">No drafts{statusFilter ? ` marked ${statusFilter}` : ''}.</div>
          ) : (
            <div className="draft-list" role="listbox" aria-label="Drafts">
              {rows.map((r) => {
                const to = recipients.byDraft.get(r.id)
                return (
                  <div
                    key={r.id}
                    role="option"
                    aria-selected={open?.id === r.id}
                    className={`draft-item${open?.id === r.id ? ' current' : ''}`}
                    onClick={() => void openDraft(r.id)}
                  >
                    <input
                      type="checkbox"
                      aria-label={`select draft ${r.id}`}
                      checked={chosen.has(r.id)}
                      disabled={closed}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const next = new Set(chosen)
                        if (e.target.checked) next.add(r.id)
                        else next.delete(r.id)
                        setChosen(next)
                      }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div className="di-top">
                        <span className="di-domain">{to?.domain ?? `account ${r.account_id}`}</span>
                        <span className="row" style={{ gap: 4, flexWrap: 'nowrap' }}>
                          {loadingDraft === r.id && <span className="spinner" />}
                          {r.has_refined && <span className="badge running" title="rewritten by the AI">AI</span>}
                          <DraftBadge status={r.status} />
                        </span>
                      </div>
                      <div className="di-sub">{r.refined_subject ?? r.original_subject}</div>
                      {to && !to.ok && <div className="di-sub" style={{ color: 'var(--danger)' }}>not sendable: {to.reason}</div>}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div>
          {open ? (
            <CompareView
              draft={open}
              busy={busy}
              closed={closed}
              view={openView}
              onView={setOpenView}
              style={style}
              position={position}
              count={rows.length}
              recipient={recipients.byDraft.get(open.id)}
              recipientsError={recipients.error}
              outcome={
                outcomeMsg ? (
                  <RefineOutcome
                    message={outcomeMsg}
                    retryDraft={retryGeneric}
                    busy={busy}
                    onRetry={(id) => void refineRow(id, true)}
                    onDismiss={() => setOutcomeMsg(null)}
                  />
                ) : null
              }
              refining={refining === open.id}
              onStep={step}
              onRefine={() => void refineRow(open.id)}
              onVerdict={decide}
            />
          ) : (
            <div className="panel"><div className="empty">Pick a draft on the left.</div></div>
          )}
        </div>
      </div>
    </>
  )
}

function DraftBadge({ status }: { status: string }) {
  const cls = status === 'approved' ? 'ok' : status === 'rejected' ? 'bad' : status === 'sent' ? 'ok' : 'catchall'
  return <span className={`badge ${cls}`}>{status}</span>
}

/**
 * What actually changed, marked up.
 *
 * The two panels above answer "what does each version say". They do not answer
 * "what did the model do", and the hardest thing to see there is a REMOVAL: a
 * dropped paragraph leaves no trace on the refined side, so the eye goes
 * straight past it. That is how a draft that had lost its entire pitch got
 * approved during testing.
 */
function DiffView({
  originalSubject,
  refinedSubject,
  originalBody,
  refinedBody,
}: {
  originalSubject: string
  refinedSubject: string
  originalBody: string
  refinedBody: string
}) {
  const subject = diffWords(originalSubject, refinedSubject)
  const body = diffWords(originalBody, refinedBody)
  const removed = body.filter((p) => p.kind === 'removed').reduce((n, p) => n + p.text.trim().split(/\s+/).filter(Boolean).length, 0)
  const added = body.filter((p) => p.kind === 'added').reduce((n, p) => n + p.text.trim().split(/\s+/).filter(Boolean).length, 0)

  return (
    <div className="panel" style={{ marginTop: 'var(--s3)', marginBottom: 0 }}>
      <h2>
        What changed
        <span className="count">
          {unchanged(body) && unchanged(subject) ? (
            'nothing — the model left it alone'
          ) : (
            <>
              <span className="diff-del">−{removed}</span>{' '}
              <span className="diff-ins">+{added}</span> words
            </>
          )}
        </span>
      </h2>
      <div className="panel-body">
        {unchanged(body) && unchanged(subject) ? (
          <p className="hint">
            Returned unchanged. That is a legitimate answer, not a failure — the prompt asks
            for no edit when the draft is already right for this account.
          </p>
        ) : (
          <>
            <div className="field">
              <label>Subject</label>
              <div className="diff mono">{render(subject)}</div>
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>Body</label>
              <div className="diff mono">{render(body)}</div>
            </div>
            <p className="hint">
              <span className="diff-del">struck through</span> was removed,{' '}
              <span className="diff-ins">highlighted</span> was added. Watch the removals: a
              whole paragraph disappearing is the change that leaves no mark on the refined
              side, and the validator rejects it only if the point was declared.
            </p>
          </>
        )}
      </div>
    </div>
  )
}

function render(parts: Part[]) {
  return parts.map((part, index) => {
    if (part.kind === 'same') return <span key={index}>{part.text}</span>
    return (
      <span key={index} className={part.kind === 'added' ? 'diff-ins' : 'diff-del'}>
        {part.text}
      </span>
    )
  })
}

function RefineOutcome({
  message,
  retryDraft,
  busy,
  onRetry,
  onDismiss,
}: {
  message: string
  retryDraft: number | null
  busy: boolean
  onRetry: (draftId: number) => void
  onDismiss: () => void
}) {
  return (
    <div className="error">
      {message}
      <div className="row" style={{ marginTop: 'var(--s2)' }}>
        {retryDraft != null && (
          <button disabled={busy} onClick={() => onRetry(retryDraft)}>
            Refine draft {retryDraft} anyway
          </button>
        )}
        <button disabled={busy} onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    </div>
  )
}

function CompareView({
  draft,
  busy,
  closed,
  view,
  onView,
  style,
  position,
  count,
  recipient,
  recipientsError,
  outcome,
  refining,
  onStep,
  onRefine,
  onVerdict,
}: {
  draft: Draft
  busy: boolean
  closed: boolean
  /** text is what it says and what changed; rendered is the email itself. */
  view: 'text' | 'rendered'
  onView: (v: 'text' | 'rendered') => void
  /** The campaign's chosen style, so this row can be seen as the recipient
   *  will actually see it — chrome, button, footer and unsubscribe link. */
  style: string | null
  position: number
  count: number
  recipient: DraftRecipient | undefined
  recipientsError: string | null
  outcome: ReactNode
  refining: boolean
  onStep: (dir: 1 | -1) => void
  onRefine: () => void
  onVerdict: (v: 'approved' | 'rejected') => void
}) {
  const [rendered, setRendered] = useState<string | null>(null)
  const [renderError, setRenderError] = useState<string | null>(null)

  useEffect(() => {
    setRendered(null)
    setRenderError(null)
    if (view !== 'rendered') return
    void (async () => {
      try {
        setRendered(await api.stylePreview(style || 'email', draft.id))
      } catch (err) {
        setRenderError(String((err as Error).message))
      }
    })()
  }, [view, style, draft.id, draft.refined_body])

  const research = (draft.compose_meta?.research ?? null) as Record<string, unknown> | null
  return (
    <div className="panel" style={{ marginBottom: 0 }}>
      <h2>
        Draft {draft.id} <DraftBadge status={draft.status} />
        <span className="count">{position} of {count}</span>
        <span className="row" style={{ marginLeft: 'auto', gap: 4 }}>
          <button disabled={position <= 1} onClick={() => onStep(-1)} title="previous (k)">↑</button>
          <button disabled={position >= count} onClick={() => onStep(1)} title="next (j)">↓</button>
        </span>
      </h2>
      <div className="panel-body">
        {outcome}

        {/* Who, before what (D116): the address this draft would go to now,
            from the same check the send runs. Account is the login; Company
            is the customer it belongs to. */}
        <dl className="facts">
          <dt>Sends to</dt>
          <dd>
            {recipientsError
              ? <span className="dim">could not resolve just now — the send re-checks every address itself</span>
              : <SendsTo row={recipient} status={draft.status} />}
          </dd>
          <dt>Site</dt>
          <dd className="mono">{recipient?.domain ?? '—'}</dd>
          <dt>Ids</dt>
          <dd className="mono">
            account {draft.account_id} · company {recipient?.company_id ?? draft.company_id ?? '—'}
          </dd>
          {/* D121: our reading of their homepage. A sensitive one is approved here, on its own. */}
          {research?.company_industry ? (
            <>
              <dt>Industry</dt>
              <dd>
                {String(research.company_industry)}
                {research.read_alone ? (
                  <span className="badge catchall" style={{ marginLeft: 6 }}
                        title="Health, finance or children's site: bulk approve skips it">
                    read on its own
                  </span>
                ) : null}
              </dd>
            </>
          ) : null}
        </dl>

        {!closed && (
          <div className="row">
            <button className="primary" disabled={busy} onClick={() => onVerdict('approved')} title="approve, then next (a)">
              Approve
            </button>
            <button className="danger" disabled={busy} onClick={() => onVerdict('rejected')} title="reject, then next (r)">
              Reject
            </button>
            <button disabled={busy} onClick={onRefine}>
              {refining ? (
                <span className="row" style={{ gap: 6 }}>
                  <span className="spinner" /> Rewriting…
                </span>
              ) : draft.refined_body ? (
                'Rewrite again with AI'
              ) : (
                'Rewrite with AI'
              )}
            </button>
            <span className="row" style={{ marginLeft: 'auto', gap: 4 }}>
              <button aria-pressed={view === 'text'} disabled={view === 'text'} onClick={() => onView('text')}>Text &amp; changes</button>
              <button aria-pressed={view === 'rendered'} disabled={view === 'rendered'} onClick={() => onView('rendered')}>Rendered email</button>
            </span>
          </div>
        )}
        {closed && (
          <div className="row">
            <button aria-pressed={view === 'text'} disabled={view === 'text'} onClick={() => onView('text')}>Text &amp; changes</button>
            <button aria-pressed={view === 'rendered'} disabled={view === 'rendered'} onClick={() => onView('rendered')}>Rendered email</button>
          </div>
        )}

        {view === 'rendered' ? (
          <>
            <p className="hint">
              Exactly what this recipient receives, in the {style || 'email'} style — rewritten text
              where it was accepted, the original where it was not, and the unsubscribe footer.
            </p>
            {renderError ? (
              <div className="error">{renderError}</div>
            ) : rendered === null ? (
              <div className="loading">
                <span className="row" style={{ gap: 8 }}>
                  <span className="spinner" /> Rendering the email…
                </span>
              </div>
            ) : (
              <iframe
                title={`draft ${draft.id} rendered`}
                srcDoc={rendered}
                sandbox=""
                style={{
                  width: '100%',
                  height: 720,
                  border: '1px solid var(--line)',
                  borderRadius: 5,
                  background: '#fff',
                }}
              />
            )}
          </>
        ) : (
          <>
            {/* The greeting is stored separately and kept out of the body so
                the model cannot rewrite it — which meant it appeared in
                NEITHER column, and a reviewer approved an email without seeing
                its first line. Shown once, above both. */}
            {draft.greeting && (
              <p className="hint">
                Opens with <span className="mono">{draft.greeting}</span> — the same in both
                versions; rewriting does not touch it.
              </p>
            )}
            <div className="grid2">
              <div>
                <label>Original — composed from approved fragments</label>
                <div className="panel" style={{ margin: 0 }}>
                  <div className="panel-body">
                    <strong>{draft.original_subject}</strong>
                    <pre className="notes">{draft.original_body}</pre>
                  </div>
                </div>
              </div>
              <div>
                <label>
                  Rewritten {draft.llm_model && <span className="mono">by {draft.llm_model}</span>}
                </label>
                <div className="panel" style={{ margin: 0 }}>
                  <div className="panel-body">
                    {draft.refined_body ? (
                      <>
                        <strong>{draft.refined_subject}</strong>
                        <pre className="notes">{draft.refined_body}</pre>
                      </>
                    ) : (
                      <div className="empty">
                        Not rewritten. Either it has not been run, or the validator rejected the
                        model's output and the original stands.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {draft.refine_meta?.angle && (
              <div className="notice">
                <strong>Why it led on that:</strong> {draft.refine_meta.angle}
                {draft.refine_meta.changes?.length ? (
                  <ul className="bullets">
                    {draft.refine_meta.changes.map((c) => (
                      <li key={c} style={{ fontSize: 12 }}>{c}</li>
                    ))}
                  </ul>
                ) : null}
                <div className="hint caption">
                  The model's own account of its judgement, so you can disagree with the reasoning
                  and not only with the result.
                  {draft.refined_at ? ` Rewritten ${new Date(draft.refined_at).toLocaleString()}.` : ''}
                </div>
              </div>
            )}

            {draft.refined_body && (
              <DiffView
                originalSubject={draft.original_subject}
                refinedSubject={draft.refined_subject ?? ''}
                originalBody={draft.original_body}
                refinedBody={draft.refined_body}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}

function SendStep({
  cycleId,
  style,
  onOpenCampaign,
  onChanged,
}: {
  cycleId: number
  style: string | null
  onOpenCampaign: () => void
  onChanged: () => void
}) {
  const [readiness, setReadiness] = useState<Awaited<ReturnType<typeof api.sendReadiness>> | null>(
    null,
  )
  const [error, setError] = useState<string | null>(null)
  const [plan, setPlan] = useState<DryRunResult | null>(null)
  const [token, setToken] = useState('')
  const [who, setWho] = useState(() => {
    try { return window.localStorage.getItem('reachout.operator') ?? '' } catch { return '' }
  })
  const [busy, setBusy] = useState<'dry' | 'send' | null>(null)
  const [result, setResult] = useState<string | null>(null)

  const load = async () => {
    try {
      setReadiness(await api.sendReadiness(cycleId))
    } catch (err) {
      setError(String((err as Error).message))
    }
  }

  useEffect(() => {
    void load()
  }, [cycleId])

  const dryRun = async () => {
    setBusy('dry')
    setError(null)
    setResult(null)
    try {
      setPlan(await api.sendDryRun(cycleId))
    } catch (err) {
      setError(String((err as Error).message))
    } finally {
      setBusy(null)
    }
  }

  const sendReal = async () => {
    setBusy('send')
    setError(null)
    setResult(null)
    try {
      const out = await api.sendReal(cycleId, token.trim(), who.trim() || 'operator')
      const parts = Object.entries(out.counted).map(([k, n]) => `${n} ${k}`)
      setResult((parts.join(', ') || 'nothing dispatched') + (out.aborted ? ` — ${out.aborted}` : ''))
      setToken('')
      onChanged()
      await load()
    } catch (err) {
      setError(String((err as Error).message))
    } finally {
      setBusy(null)
    }
  }

  if (error && !readiness) return <div className="error">{error}</div>
  if (!readiness) return <div className="loading">Checking…</div>

  const policy = readiness.policy ?? {}
  const realRoute = !!readiness.real_send_route_registered

  return (
    <>
    <StylePreview cycleId={cycleId} chosen={style} onOpenCampaign={onOpenCampaign} />
    <TestInboxPanel cycleId={cycleId} redirectEnabled={!!readiness.redirect_route_registered} />
    <div className="panel">
      <h2><StepN n="3" /> Send to customers</h2>
      <div className="panel-body">
        {error && <div className="error">{error}</div>}
        {result && <div className="notice">{result}</div>}

        {policy.dispatch_mode === 'auto' && (
          <div className={policy.armed ? 'notice warn' : 'notice'}>
            <strong>This campaign is set to automatic dispatch.</strong>{' '}
            {policy.armed
              ? 'It is ARMED: the scheduler will send the approved drafts of this cycle on its next pass, if every blocker below is clear on its process.'
              : 'It is not armed, so the scheduler sends nothing. A person arms it on the campaign screen; a manual send from here still works.'}
          </div>
        )}

        <div className={readiness.can_send ? 'notice' : 'error'}>
          <strong>{readiness.can_send ? 'No blockers' : `${readiness.blockers.length} blockers`}</strong>
          <ul className="bullets">
            {readiness.blockers.map((b) => (
              <li key={b} style={{ fontSize: 12.5 }}>{b}</li>
            ))}
          </ul>
        </div>

        {!!(readiness.approvals?.approved_by_agent || readiness.approvals?.approved_by_scheduler) && (
          <div className="notice" style={{ fontSize: 12.5 }}>
            <strong>
              Of {readiness.approvals.approved} approved drafts, {readiness.approvals.approved_by_agent} were
              approved by an agent and {readiness.approvals.approved_by_scheduler} by the scheduler's
              validator, not by a person.
            </strong>{' '}
            <code>reviewed_by</code> on each draft says which.
          </div>
        )}

        <p className="hint">{readiness.note}</p>

        {realRoute ? (
          <>
            <div className="row-form">
              <button type="button" disabled={busy !== null} onClick={() => void dryRun()}>
                {busy === 'dry' ? 'Checking…' : 'Dry-run the customer send'}
              </button>
              {plan && (
                <span className="hint">
                  {plan.cleared} of {plan.approved} approved drafts cleared every check. {plan.note}
                  {plan.identity && (
                    <>
                      {' '}Service: {plan.identity.approved_by_company} of {plan.approved} approved
                      drafts came in by company; {plan.identity.refused_by_identity_check} refused by
                      the login re-check (D116).
                    </>
                  )}
                </span>
              )}
            </div>
            {plan && plan.cleared > 0 && (
              <div className="row-form" style={{ marginTop: 10 }}>
                <div style={{ minWidth: 200 }}>
                  <label htmlFor="who-send">Your name</label>
                  <input id="who-send" type="text" value={who} onChange={(e) => setWho(e.target.value)} />
                </div>
                <div style={{ minWidth: 220 }}>
                  <label htmlFor="tok">
                    Confirm token <span className="label-note">— type it: cycle id, colon, cleared count</span>
                  </label>
                  <input id="tok" type="text" value={token} onChange={(e) => setToken(e.target.value)} placeholder={`${cycleId}:…`} spellCheck={false} autoComplete="off" />
                </div>
                <button
                  type="button"
                  className="danger"
                  disabled={busy !== null || !token.trim() || !readiness.can_send}
                  onClick={() => void sendReal()}
                >
                  {busy === 'send' ? 'Sending…' : 'Send to customers'}
                </button>
              </div>
            )}
          </>
        ) : (
          <button className="primary" disabled title="REACHOUT_GUI_ALLOW_REAL_SEND is not set on this process">
            Send for real
          </button>
        )}
      </div>
    </div>
    <AuthorizePanel cycleId={cycleId} onChanged={onChanged} />
    </>
  )
}

/** A numbered step heading on the Send pane — the order is the advice. */
function StepN({ n }: { n: string }) {
  return <span className="brief-n">{n}</span>
}

/**
 * Who each draft goes to, resolved NOW (D116). The server answers with the same
 * recipient check dispatch runs, so the address here is the address that would
 * be used — a person can verify a recipient by eye before approving or
 * authorizing. A draft a gate refuses shows why instead of an address. Never
 * stored; fetched for this screen.
 */
function useRecipients(cycleId: number, refresh = 0) {
  const [byDraft, setByDraft] = useState<Map<number, DraftRecipient>>(new Map())
  const [error, setError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const reload = async () => {
    try {
      const out = await api.sendRecipients(cycleId)
      setByDraft(new Map(out.drafts.map((d) => [d.draft_id, d])))
      setError(null)
    } catch (err) {
      setError(String((err as Error).message))
    } finally {
      setLoaded(true)
    }
  }
  useEffect(() => {
    void reload()
  }, [cycleId, refresh])
  return { byDraft, error, loaded, reload }
}

function RecipientsNote({ state }: { state: ReturnType<typeof useRecipients> }) {
  if (state.error) {
    return (
      <div className="error" style={{ fontSize: 12.5 }}>
        Could not resolve the To addresses just now ({state.error}). Nothing is sent from this
        screen; the send re-checks every address itself.
      </div>
    )
  }
  return (
    <p className="hint">
      <strong>Sends to</strong> is the address each draft would go to now, from the same check the
      send runs. Account is the login; Company is the customer it belongs to.
    </p>
  )
}

function SendsTo({ row, status }: { row: DraftRecipient | undefined; status: string }) {
  if (!row) return <span className="hint">{status === 'sent' || status === 'rejected' ? '—' : '…'}</span>
  if (row.to && row.ok) return <span className="mono">{row.to}</span>
  return (
    <span className="badge bad" title={row.reason ?? undefined} style={{ whiteSpace: 'normal' }}>
      {row.to ? <span className="mono">{row.to} — </span> : null}
      not sendable: {row.reason}
    </span>
  )
}

/** The approved drafts a person is about to authorize, with who each goes to. */
function RecipientTable({ cycleId, refresh }: { cycleId: number; refresh: number }) {
  const state = useRecipients(cycleId, refresh)
  const approved = [...state.byDraft.values()].filter((d) => d.status === 'approved')
  if (state.error) return <RecipientsNote state={state} />
  if (!state.loaded) return <div className="loading">Resolving addresses…</div>
  if (approved.length === 0) return null
  return (
    <table style={{ marginBottom: 10 }}>
      <thead>
        <tr>
          <th className="num">Draft</th>
          <th className="num" title="Account.AccountID — the login">Account</th>
          <th className="num" title="Company.CompanyID — the customer">Company</th>
          <th>Domain</th>
          <th>Sends to</th>
        </tr>
      </thead>
      <tbody>
        {approved.map((d) => (
          <tr key={d.draft_id}>
            <td className="num mono">{d.draft_id}</td>
            <td className="num mono">{d.account_id}</td>
            <td className="num mono">{d.company_id ?? '—'}</td>
            <td className="mono">{d.domain ?? '—'}</td>
            <td><SendsTo row={d} status={d.status} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/**
 * "Review the drafts, then tell the agent to go" (D108). A person reads the
 * approved drafts on the Review step, then authorizes that exact batch here. The
 * agent may then release it once, within the expiry. Editing or approving a draft
 * afterwards voids it. Nothing here sends mail.
 */
function AuthorizePanel({ cycleId, onChanged }: { cycleId: number; onChanged: () => void }) {
  const [state, setState] = useState<SendAuthorizationState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [ttl, setTtl] = useState(60)
  const [who, setWho] = useState(() => {
    try { return window.localStorage.getItem('reachout.operator') ?? '' } catch { return '' }
  })

  // Bumped on every reload, so the address table below re-resolves whenever
  // the approved count it sits beside does.
  const [loads, setLoads] = useState(0)
  const load = async () => {
    try {
      setState(await api.sendAuthorization(cycleId))
      setLoads((n) => n + 1)
    } catch (err) {
      setError(String((err as Error).message))
    }
  }
  useEffect(() => {
    void load()
  }, [cycleId])

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      await fn()
      setResult(done)
      onChanged()
      await load()
    } catch (err) {
      setError(String((err as Error).message))
    } finally {
      setBusy(false)
    }
  }

  if (!state) return error ? <div className="error">{error}</div> : null
  const { policy, active } = state
  const when = (iso: string) => new Date(iso).toLocaleString()

  return (
    <div className="panel">
      <h2><StepN n="or" /> Let an agent send this batch</h2>
      <div className="panel-body">
        {error && <div className="error">{error}</div>}
        {result && <div className="notice">{result}</div>}

        {!policy.agent_release_allowed ? (
          <p className="hint">
            This campaign is not open to agent release. Open it under Sending on the campaign
            page, after approving its copy.
          </p>
        ) : active ? (
          <div className="notice warn">
            <strong>Authorized.</strong> {active.drafts} drafts, at most {active.cleared} recipients,
            by {active.authorized_by}, until {when(active.expires_at)}. Tell the agent to go and it
            sends this batch once. Editing or approving any draft first voids it.{' '}
            <button
              type="button"
              disabled={busy}
              onClick={() => void act(() => api.revokeSendAuthorization(active.id, who.trim() || 'operator'), 'authorization withdrawn')}
            >
              Withdraw
            </button>
          </div>
        ) : (
          <>
            <RecipientTable cycleId={cycleId} refresh={loads} />
            <p className="hint">
              Read the approved drafts first. Authorizing says you have read these {state.approved_drafts}{' '}
              and the agent may send exactly them, once. It runs the dry run now, so the ceiling is
              the number that clears suppression and cadence today.
            </p>
            <div className="row-form">
              <div style={{ minWidth: 200 }}>
                <label htmlFor="who-authorize">Your name</label>
                <input id="who-authorize" type="text" value={who} onChange={(e) => setWho(e.target.value)} />
              </div>
              <div style={{ minWidth: 140 }}>
                <label htmlFor="ttl-authorize">Valid for <span className="label-note">minutes</span></label>
                <input
                  id="ttl-authorize" type="number" min={1} max={240} value={ttl}
                  onChange={(e) => setTtl(Math.max(1, Math.min(240, Number(e.target.value) || 60)))}
                />
              </div>
              <button
                type="button"
                className="danger"
                disabled={busy || !who.trim() || state.approved_drafts === 0}
                onClick={() => void act(
                  () => api.sendAuthorize(cycleId, who.trim(), ttl),
                  'authorized — tell the agent to go',
                )}
              >
                {busy ? 'Authorizing…' : `Authorize these ${state.approved_drafts} drafts`}
              </button>
            </div>
          </>
        )}
        {!state.release_route_registered && (
          <p className="hint">
            The release route is off on this process (REACHOUT_GUI_ALLOW_REAL_SEND is not set), so
            an authorization cannot be spent here yet.
          </p>
        )}

        {state.history.length > 0 && (
          <details style={{ marginTop: 'var(--s3)' }}>
            <summary>History</summary>
            <ul className="bullets">
              {state.history.map((h) => (
                <li key={h.id} style={{ fontSize: 12.5 }}>
                  {when(h.authorized_at)} · {h.authorized_by} · {h.drafts} drafts, ≤{h.cleared} ·{' '}
                  {h.used_at ? `released by ${h.released_by}` : h.revoked_at ? `withdrawn by ${h.revoked_by}` : new Date(h.expires_at) < new Date() ? 'expired' : 'live'}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  )
}

/**
 * Deliver the approved drafts to an operator inbox. The address comes from a
 * committed allowlist — a free-text field is how a customer gets mail.
 *
 * The confirm token is typed, never filled in. The dry-run result names the
 * cleared count.
 */
function TestInboxPanel({
  cycleId,
  redirectEnabled,
}: {
  cycleId: number
  redirectEnabled: boolean
}) {
  const [recipients, setRecipients] = useState<DebugRecipient[] | null>(null)
  const [chosen, setChosen] = useState('')
  const [plan, setPlan] = useState<DryRunResult | null>(null)
  const [busy, setBusy] = useState<'dry' | 'send' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const res = await api.debugRecipients()
        setRecipients(res.recipients)
        setChosen((current) => current || res.recipients[0]?.address || '')
      } catch (err) {
        setError(String((err as Error).message))
      }
    })()
  }, [])

  const dryRun = async () => {
    setBusy('dry')
    setError(null)
    setResult(null)
    try {
      setPlan(await api.sendDryRun(cycleId, { for_redirect: true }))
    } catch (err) {
      setError(String((err as Error).message))
    } finally {
      setBusy(null)
    }
  }

  const send = async () => {
    if (!chosen || !plan) return
    setBusy('send')
    setError(null)
    setResult(null)
    try {
      const out = await api.sendRedirect(cycleId, {
        cleared: plan.cleared,
        redirect_to: chosen,
      })
      const parts = Object.entries(out.counted).map(([k, n]) => `${n} ${k}`)
      setResult(
        (parts.join(', ') || 'nothing dispatched') +
          (out.aborted ? ` — ${out.aborted}` : ''),
      )
    } catch (err) {
      setError(String((err as Error).message))
    } finally {
      setBusy(null)
    }
  }

  const why = recipients?.find((r) => r.address === chosen)?.why
  const overCap = !!plan?.over_cap
  // A dry run is still required — it is what resolves every address against
  // live suppression and cadence, and its cleared count is what the send is
  // checked against. Removing the typed token did not remove that.
  const canSend =
    redirectEnabled &&
    !!chosen &&
    !!plan &&
    !overCap &&
    plan.cleared > 0 &&
    busy === null

  return (
    <div className="panel">
      <h2><StepN n="2" /> Send to the test inbox first</h2>
      <div className="panel-body">
        <p className="hint">
          Every approved draft that clears suppression arrives at one allowlisted
          address, not at the customer. Cadence does not move. The cycle stays
          unsent, and nothing here starts a cooldown.
        </p>

        {!redirectEnabled && (
          <div className="error">
            <span className="mono">REACHOUT_GUI_ALLOW_REDIRECT</span> is not set, so{' '}
            <span className="mono">/api/send/redirect</span> has no handler. Set it to{' '}
            <span className="mono">1</span> in <span className="mono">.env</span> and restart.
            Leave <span className="mono">REACHOUT_ALLOW_SEND</span> unset.
          </div>
        )}

        {error && <div className="error">{error}</div>}
        {result && <div className="notice">{result}</div>}

        <div className="row-form">
          <div style={{ minWidth: 280 }}>
            <label htmlFor="test-inbox">Test inbox</label>
            <select
              id="test-inbox"
              value={chosen}
              disabled={!recipients}
              style={{ width: '100%' }}
              onChange={(e) => setChosen(e.target.value)}
            >
              {(recipients ?? []).map((r) => (
                <option key={r.address} value={r.address}>
                  {r.address}
                </option>
              ))}
            </select>
          </div>
          <button type="button" disabled={busy !== null} onClick={() => void dryRun()}>
            {busy === 'dry' ? 'Checking…' : 'Dry-run'}
          </button>
        </div>
        {why && <p className="hint">{why}</p>}

        {plan && (
          <p className={overCap ? 'error' : 'hint'}>
            {plan.cleared} of {plan.approved} approved drafts cleared every check
            {chosen ? (
              <>
                {' '}
                and would all arrive at <span className="mono">{chosen}</span>
              </>
            ) : null}
            . Cap 20.
            {overCap ? ' Approve fewer drafts, then dry-run again.' : null} {plan.note}
          </p>
        )}

        <div className="row-form" style={{ marginTop: 10 }}>
          <button
            type="button"
            className="primary"
            disabled={!canSend}
            onClick={() => void send()}
          >
            {busy === 'send' ? 'Sending…' : 'Send to test inbox'}
          </button>
          {!plan && (
            <span className="hint caption">
              Dry-run first — it resolves every address against live
              suppression and cadence, and this sends the count it reports.
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * How this cycle's mail looks, previewed on its first draft.
 *
 * Read-only on purpose. The style is ONE choice for the whole campaign and
 * changing it withdraws copy approval, so a picker on a cycle page was a
 * campaign-wide edit hiding inside one batch. It is changed on the campaign's
 * Setup → Look; this shows what it produces. To see a particular recipient's
 * email, use Rendered email on that draft in Review.
 */
function StylePreview({
  cycleId,
  chosen,
  onOpenCampaign,
}: {
  cycleId: number
  chosen: string | null
  onOpenCampaign: () => void
}) {
  const [fallback, setFallback] = useState<string>('')
  const [against, setAgainst] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const effective = chosen || fallback

  useEffect(() => {
    void (async () => {
      try {
        const res = await api.styles()
        setFallback(res.default)
        setAgainst(res.styles.find((s: StyleInfo) => s.name === (chosen || res.default))?.against ?? null)
      } catch (err) {
        setError(String((err as Error).message))
      }
    })()
  }, [chosen])

  useEffect(() => {
    if (!effective || !open) return
    setPreview(null)
    void (async () => {
      try {
        const draftId = await firstDraftId(cycleId)
        setPreview(await api.stylePreview(effective, draftId))
      } catch (err) {
        setError(String((err as Error).message))
      }
    })()
  }, [effective, cycleId, open])

  return (
    <div className="panel">
      <h2>
        <StepN n="1" /> Check how it looks
        <span className="count mono">{chosen ?? `${fallback} (default)`}</span>
      </h2>
      <div className="panel-body">
        {error && <div className="error">{error}</div>}
        <p className="hint">
          Every draft in this cycle sends in the <span className="mono">{effective || '…'}</span> style
          {chosen ? '' : ', the default, because the campaign has not chosen one'}.{' '}
          <button className="linkish" onClick={onOpenCampaign}>Change it on the campaign</button>{' '}
          (that withdraws copy approval).
        </p>
        {against && <p className="hint"><strong>Not for:</strong> {against}</p>}
        <button onClick={() => setOpen(!open)}>{open ? '▾ Hide preview' : '▸ Preview the first draft'}</button>
      </div>
      {open && (preview === null ? (
        <div className="loading">Rendering the first draft…</div>
      ) : (
        <iframe
          title="style preview"
          srcDoc={preview}
          sandbox=""
          style={{ width: '100%', height: 620, border: 0, borderTop: '1px solid var(--line)', background: '#fff' }}
        />
      ))}
    </div>
  )
}
